import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { INTERNAL_EVENTS_QUERY_KEY } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/EventsOverviewButtonAndModal";
import { EventGroup } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { GlobalEventType } from "shared/types/operationalInformation";

/**
 * Retrieve internal messages of eventgroup ("train" | "station"), null returns everything.
 */
export const useInternalMessagesEventGroup = (
  eventGroup: EventGroup | null,
) => {
  const internalMessagesType: GlobalEventType = "GLOBAL_INTERNAL_INFO";
  const eventGroupQuery = eventGroup ? `&eventGroup=${eventGroup}` : ``;
  const countryCode = useCountryCodeForDropsRole();

  const internalMessageEvents = useQuery({
    queryKey: [INTERNAL_EVENTS_QUERY_KEY, eventGroup, countryCode],
    refetchInterval: 60000,
    queryFn: ({ signal }) =>
      axiosClient
        .get<InternalMessageResponse[]>(
          `${getBackendUrl()}/global-information/visible/${countryCode}?type=${internalMessagesType}${eventGroupQuery}`,
          {
            signal,
          },
        )
        .then((res) =>
          res.data.sort((a, b) => {
            const dateA = new Date(a.updatedAt).valueOf();
            const dateB = new Date(b.updatedAt).getTime();
            return dateB - dateA;
          }),
        ),
  });
  return internalMessageEvents;
};

/**
 * Retrieve internal messages of relatedEvents by their uuid ("trainevent" | "stationevent" | "infrastructure" | "global")
 */
export const useInternalMessagesByUuids = (
  uuids: string[] = [],
): InternalMessagesByUuids => {
  const uuidsQuery = uuids?.map((uuid) => `uuids=${uuid}`).join("&");
  const internalMessageEvents = useQuery({
    queryKey: [INTERNAL_EVENTS_QUERY_KEY, ...uuids],
    staleTime: 0,
    enabled: uuids.length > 0,
    queryFn: ({ signal }) =>
      axiosClient
        .get<
          InternalMessageResponse[][]
        >(`${getBackendUrl()}/global-information/internal-info/events?${uuidsQuery}`, { signal })
        .then((res) => res.data),
  });
  return Object.fromEntries(
    uuids.map((uuid, i) => [uuid, internalMessageEvents.data?.[i]]),
  );
};

export type InternalMessagesByUuids = {
  [val: string]: InternalMessageResponse[] | undefined;
};

// Retrieve all internal messages
export const useInternalMessages = () => useInternalMessagesEventGroup(null);
